<template>
  <div class="p-4 review-page mb-16">
    <div class="header-section flex justify-between items-center">
      <div v-if="submission.commit">
        <h3>
          <span
            class="text-sm text-normal rounded-sm tag uppercase"
            :class="
              submission.review_state === 'pending'
                ? 'bg-orange-light'
                : submission.review_state === 'completed'
                ? 'bg-green text-white'
                : ''
            "
            >{{ submission.review_state }}</span
          >
          Submission: {{ submission.commit.message }} :
          {{ submission.commit.sha.substr(0, 8) }}
        </h3>
        <div class="text-xs">
          {{ getSubmissionDate(submission.updated_on) }}
        </div>
        <div>
          <span class="font-bold mr-2">Deadline:</span>
          {{ getSubmissionDeadline(submission.updated_on) }}
        </div>
        <div v-if="submission.review_state === 'completed'">
          <span class="font-bold mr-2">
            Evaluation Result:
          </span>
          <span
            class="tag text-white uppercase font-bold"
            :class="
              submission.evaluation_state === 'passed'
                ? 'bg-green'
                : 'bg-red-light'
            "
            >{{ submission.evaluation_state }}</span
          >
        </div>
        <div>
          <span class="font-bold mr-2">User Repo:</span>
          <a :href="submission.commit.repo" target="_blank" class="text-blue">{{
            submission.commit.repo
          }}</a>
        </div>
        <div>
          <span class="font-bold mr-2">Live Link:</span>
          <a
            :href="submission.context.live_link"
            target="_blank"
            class="text-blue"
            >{{ submission.context.live_link }}</a
          >
        </div>
      </div>
      <div>
        <div v-if="submission.user" class="user-card">
          <div class="flex justify-start items-center my-2">
            <div class="mr-2 font-bold">Author:</div>
            <img class="mr-2 ml-4" :src="submission.user.profile_pic_url" />
            {{ submission.user.first_name }} {{ submission.user.last_name }}
          </div>
        </div>
        <div v-if="submission.reviewer" class="user-card">
          <div class="flex justify-start items-center my-2">
            <div class="mr-2 font-bold">Reviewer:</div>
            <img class="mr-2" :src="submission.reviewer.profile_pic_url" />
            {{ submission.reviewer.first_name }}
            {{ submission.reviewer.last_name }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="Object.keys(submission).length > 0" class="mt-6">
      <SubmissionTestCases
        :testCases="submission.testcases"
        :quillEditorOptions="quillEditorOptions"
        @UPDATE_CONTENT="updateContent"
        @UPDATE_TESTCASE_STATUS="updateTestCaseStatus"
      />
      <label>Additional comment for entire submission.</label>
      <QuillEditor
        ref="myQuillEditor"
        v-model="submission.comment"
        class="comment-editor w-full"
        :options="quillEditorOptions"
      />
    </div>
    <div class="text-right mt-6">
      <button class="bg-green text-white px-8 py-2 mt-4" @click="submitReview">
        Submit Review
      </button>
    </div>
  </div>
</template>

<script>
import { quillEditor } from "vue-quill-editor"
import SubmissionTestCases from "@/components/ProjectSubmission/SubmissionTestCases"
import siteConfig from "@/utils/siteConfig"
import "quill/dist/quill.core.css"
import "quill/dist/quill.snow.css"
import "quill/dist/quill.bubble.css"

export default {
  components: {
    SubmissionTestCases,
    QuillEditor: quillEditor,
  },
  data() {
    return {
      submission: {},
      quillEditorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            ["blockquote", "code-block"],
            [{ indent: "-1" }, { indent: "+1" }],
            ["clean"],
          ],
        },
        placeholder: "Enter your comments here...",
        readOnly: false,
        theme: "snow",
      },
    }
  },
  created() {
    this.getSubmission()
  },
  methods: {
    updateContent(payload) {
      this.submission.testcases.sections[payload.sectionIndex].tests[
        payload.testCaseIndex
      ].comment = payload.content
    },
    updateTestCaseStatus(payload) {
      this.submission.testcases.sections[payload.sectionIndex].tests[
        payload.testCaseIndex
      ].passed = payload.passed
    },
    getSubmission() {
      const { submissionId } = this.$route.params
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}questions/submissions/${submissionId}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const { submission } = res.data
        // change to repo url
        submission.commit.repo = submission.commit.repo
          .replace("api.", "")
          .replace("/repos", "")

        let result = {}
        if (submission.testcases) {
          if (typeof submission.testcases === "object") {
            result = submission.testcases
          } else {
            try {
              const obj = JSON.parse(submission.testcases)
              result = obj
            } catch (err) {
              console.log(err)
              result = {}
            }
          }
        }
        submission.testcases = result
        submission.testcases.sections.forEach(section => {
          section.tests.forEach(test => {
            /* for pending reviews these two keys won't be present */
            if (!Object.prototype.hasOwnProperty.call(test, "comment")) {
              test.comment = ""
            }
            if (!Object.prototype.hasOwnProperty.call(test, "passed")) {
              test.passed = false
            }
          })
        })

        this.submission = submission
      })
    },
    async submitReview() {
      if (this.submission.reviewer && this.submission.reviewer.email) {
        const v = await this.$swal({
          icon: "info",
          title: "Hmm!",
          text: `Are you sure you want to submit?`,
          buttons: {
            cancel: "No!",
            confirm: {
              text: "Yes",
              value: true,
            },
          },
        })
        if (!v) {
          return
        }
        this.submission.reviewer_email = this.submission.reviewer.email

        const { submissionId } = this.$route.params
        this.axios({
          method: "patch",
          url: `${siteConfig.siteURL}questions/submissions/${submissionId}`,
          data: this.submission,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "x-access-token": localStorage.token,
          },
        })
          .then(res => {
            this.$noty.success(res.data.message)
          })
          .catch(err => {
            this.$noty.error(err.response.data.message)
          })
      } else {
        this.$swal({
          icon: "error",
          title: "Uhh! Hoo!",
          text: `Reviewer isn't assigned for this submission. Can't submit without a reviewer.`,
          buttons: {
            confirm: true,
            cancel: false,
          },
        })
      }
    },
    getSubmissionDate(date) {
      const d = new Date(date)
      const timeStr = d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      const dateStr = d.toLocaleDateString("en-GB")
      return `${timeStr}, ${dateStr}`
    },
    getSubmissionDeadline(date) {
      if (date) {
        let t = new Date(date).getTime()
        t += 86400 * 1000
        return this.getSubmissionDate(t)
      }
      return ""
    },
  },
}
</script>

<style scoped>
.tag {
  padding: 2px 0.25rem;
}

.review-page {
  text-shadow: none;
  line-height: 1.5;
}

.user-card img {
  width: 2rem;
  height: 2rem;
  border-radius: 5px;
}
</style>
